import React, { useEffect, useMemo, useRef, useState } from "react";

// components
import Header from "../../components/Analytics/Common/Header";
import SectionHeader from "../../components/Analytics/Common/SectionHeader";
import MetricCard from "../../components/Analytics/Common/MetricCard";
import MetricSection from "../../components/Analytics/Common/MetricSection";
import { Topbar } from "../../components/_commons/Topbar";
import ChartMetric from "../../components/Analytics/Common/ChartMetric";
import MultiChartMetrics from "../../components/Analytics/Common/MultiChartMetrics";
import MetricHeader from "../../components/Analytics/Common/MetricHeader";
import DownloadButton from "../../components/Analytics/Common/DownloadButton";
import Line from "../../components/NivoCharts/Line";
import ChartSelector from "../../components/Analytics/Common/ChartSelector";
import Filters, { BreakdownByFilter } from "../../components/Analytics/Common/Filters";
import Pie from "../../components/NivoCharts/Pie";
import { CustomTable } from "../../components/_commons/CustomTable";
import { HorizonBanner } from "@urbanpiper-engineering/horizon";

// helpers
import {
	captureScreenshot,
	customerAnalyticsBanner,
	generateDurationText,
	getDurationPreset,
	trackPNGDownload
} from "../../helpers/analytics";

// thrid party
import { connect } from "react-redux";
import he from "he";
import { useLocation } from "react-router-dom";

// utils
import { adjustNestedContainer, commifyNumbers, getSortOrder, lS, scroll, trackEvent } from "../../atlas-utils";

// actions
import {
	fetchCustomerBreakdownTable,
	fetchCustomersBreakdownChart,
	fetchNewCustomersBreakdownChart,
	fetchNewCustomersBreakdownTable,
	fetchActiveCustomerMetrics,
	fetchActiveCustomersBreakdownChart,
	getAllAppliedFilters,
	updateCustomerAnalyticsState,
	fetchRepeatCustomerMetrics,
	fetchAndGenerateCSVReport,
	fetchInActiveCustomerMetrics
} from "../../actions/analytics";

// constants
import { ANALYTICS_DEFAULT_COLORS, ANALYTICS_SECTIONS, NESTED_ENTITY_TYPES } from "../../client-config";
import SatismeterService from "../../services/SatismeterService";
import { Share } from "../../components/Analytics/Common/Share";
import { NestedEntityContainer } from "../../components/_commons/NestedEntityContainer";
import { store } from "../../store/configureStore";

const NESTED_ENTITY_INITIAL_STATE = {
	show: false,
	type: null,
	id: null
};

const Customers = ({
	customerAnalytics,
	bizId,
	isMultibrandEnabled = false,
	analyticsReportState,
	analyticsFiltersState,
	configItems,
	login
}) => {
	const { search } = useLocation();

	const [isPageScrolled, setPageScrolled] = useState(document?.documentElement?.scrollTop > 60 || false);
	const query = useMemo(() => new URLSearchParams(search), [search]);

	const handlePageScroll = () => {
		if (document.documentElement.scrollTop > 60) {
			setPageScrolled(true);
		} else {
			setPageScrolled(false);
		}
	};

	useEffect(() => {
		if (query.get("metric")) {
			switchTab({ value: query.get("metric") });
		} else {
			document.documentElement.scrollTop = 0;
		}
		trackEvent("analytics_module", { module: "Customers" });
		window.addEventListener("scroll", handlePageScroll);
		SatismeterService.satismeterEvent("customerModuleAccessed");
		return () => {
			window.removeEventListener("scroll", handlePageScroll);
		};
	}, []);
	const { appliedFilters } = analyticsFiltersState;
	const areMultipleBrandsSelected =
		Boolean(appliedFilters?.brand_id?.find((brand) => brand === "all")) || appliedFilters?.brand_id?.length > 1;
	const areMultiplePlatformsSelected =
		Boolean(appliedFilters?.platform_names?.find((plf) => plf === "all")) ||
		appliedFilters?.platform_names?.length > 1;
	const [currTab, setCurrTab] = useState(ANALYTICS_SECTIONS["customers"]?.["tabs"]?.[0]?.value);
	const refs = {
		customer_summary: useRef(),
		active_customers: useRef(),
		customers_breakdown: useRef(),
		new_customers_breakdown: useRef()
	};

	const switchTab = (tab, tabClick = false) => {
		setCurrTab(tab.value);
		window.requestAnimationFrame(() => {
			scroll(
				{
					top: refs[tab.value]?.current?.offsetTop - (document.querySelector(".with-banner") ? 290 : 230),
					left: 0
				},
				window,
				"instant"
			);
		});
		// track event
		if (tabClick) {
			trackEvent("analytics_tab_clicked", {
				module: "Customers",
				tab: tab.label
			});
		}
	};

	const renderCenterMetricValue = (props) => {
		let total = 0;
		props.dataWithArc.forEach((datum) => {
			total += Math.round(datum.value);
		});
		return `${commifyNumbers(total)}`;
	};

	const generateImage = (key, title = "Revenue", subChart) => {
		const durationObject = getAllAppliedFilters()?.durationObject?.duration;
		const comparisonDuration = getAllAppliedFilters()?.durationObject?.comparisonDuration;

		let timeDuration = durationObject?.preset
			? `${getDurationPreset(durationObject?.preset)?.label}`
			: durationObject?.preset ||
				`${generateDurationText(durationObject?.custom?.startDate, durationObject?.custom?.endDate)}`;
		timeDuration += `${comparisonDuration ? ` / Compared to ${generateDurationText(comparisonDuration?.startDate, comparisonDuration?.endDate)}` : ""}`;

		if (key === "customers_breakdown" || key == "active_customers") {
			const metricSectionElement = refs[key]?.current?.querySelector(`.metric-section .nivo-chart-${subChart}`);
			if (metricSectionElement) {
				let elementRef = { current: metricSectionElement };
				captureScreenshot(elementRef, title, timeDuration).then((res) => {
					trackPNGDownload(key, subChart);
				});
			}
		} else if (key === "new_customers_breakdown") {
			const metricSectionElement = refs[key]?.current?.querySelector(".metric-section .chart-container");
			if (metricSectionElement) {
				let elementRef = { current: metricSectionElement };
				captureScreenshot(elementRef, title, timeDuration).then((res) => {
					trackPNGDownload(key, subChart);
				});
			}
		} else {
			captureScreenshot(refs[key], title, timeDuration).then((res) => {
				trackPNGDownload(key, subChart);
			});
		}
	};
	const nestedRef = useRef();
	const [nestedEntity, setNestedEntity] = useState(NESTED_ENTITY_INITIAL_STATE);
	const handleNestedEntity = (toOpen = false, type, id) => {
		if (!toOpen) {
			setNestedEntity(NESTED_ENTITY_INITIAL_STATE);
		} else {
			setNestedEntity({
				show: true,
				type,
				id
			});
		}
		adjustNestedContainer(toOpen);
	};
	const [isFormOpen, setIsFormOpen] = useState(false);
	const handleCancel = () => {
		setIsFormOpen(false);
	};
	const linkShareBtn = (
		<div onClick={() => setIsFormOpen(true)} className="share">
			Share
		</div>
	);
	const handlePiperAcademy = () => {
		store.dispatch({
			type: "UPDATE_PIPER_ACADEMY_STATE",
			payload: {
				location: "analytics",
				start: "0:0"
			}
		});
		handleNestedEntity(true, NESTED_ENTITY_TYPES[13], "");
	};
	const lsAnalytics = lS.get("analytics") || {};
	const [textBannerDismissed, setTextBannerDismissed] = useState(lsAnalytics?.customers?.[bizId]?.textBanner);

	const dismissTextBanner = () => {
		let lsAnalytics = lS.get("analytics") || {};
		if (!lsAnalytics.customers) {
			lsAnalytics.customers = {};
		}
		if (!lsAnalytics.customers[bizId]) {
			lsAnalytics.customers[bizId] = {};
		}
		lsAnalytics.customers[bizId] = { ...lsAnalytics.customers[bizId], textBanner: true };
		lS.set("analytics", lsAnalytics);
		setTextBannerDismissed(true);
	};
	const country = login?.loggedInbizDetail?.country;
	const banner = customerAnalyticsBanner(country)?.banner;
	const bannerText = customerAnalyticsBanner(country)?.bannerText;
	const bannerToolTip = customerAnalyticsBanner(country)?.bannerToolTip;
	const bannerToolTipDesc = customerAnalyticsBanner(country)?.bannerToolTipDesc;

	const blockedPlatforms = ["mrsool", "toyou", "ninja", "retter", "hungrypanda", "justeat", "grubhub"];
	const bizPlatforms = configItems?.bizPlatforms?.items || [];

	const blockedPltIds = bizPlatforms?.reduce((acc, plt) => {
		if (
			blockedPlatforms.includes(plt.platformName.toLowerCase()) &&
			plt.id !== "all" &&
			plt.platformName !== "All Platforms"
		) {
			acc.push(plt.id);
		}
		return acc;
	}, []);

	const appliedPlatformIds = (appliedFilters?.platform_names || []).map(String);

	const allBlockedPltSelected =
		appliedPlatformIds &&
		appliedPlatformIds.length > 0 &&
		appliedPlatformIds.every((id) => blockedPltIds.includes(id));

	return (
		<div className="analytics-customer section-container-common">
			{!textBannerDismissed && banner && (
				<HorizonBanner
					className="customer-analytics-banner"
					status="info"
					onClose={dismissTextBanner}
					bannerHeading={<span>{bannerText}</span>}
				/>
			)}
			<Header>
				<SectionHeader
					title="Customer Analytics"
					showHelpBtn
					isPageScrolled={isPageScrolled}
					headerRight={linkShareBtn}
					handlePiperAcademy={handlePiperAcademy}
					{...(banner || bannerToolTip
						? {
								description: "Customer analytics supports limited platforms",
								descToolTip: bannerToolTipDesc || bannerText
							}
						: {})}
				/>
				<Filters showBrands showLocations showPlatforms showDateCompare module="Catalogue" />

				<Topbar
					tabs={ANALYTICS_SECTIONS["customers"]["tabs"]}
					selectedTab={currTab}
					switchTab={(tab) => switchTab(tab, true)}
				/>
			</Header>
			<Share module="revenue" handleCancel={handleCancel} isOpen={isFormOpen} />
			<NestedEntityContainer
				show={nestedEntity.show}
				type={nestedEntity.type}
				id={nestedEntity.id}
				closeNestedContainer={() => handleNestedEntity(false)}
				nestedRef={nestedRef}
				isNested={false}
				isForeignSource={true}
			/>

			{allBlockedPltSelected ? (
				<div className="banned-info">
					<img className="img" src="/assets/empty_states/graphics-empty-dashboard.svg" />
					<div className="info-text">
						Customer Analytics isn’t available for selected platforms & dates before Jan 1st, 2025.
					</div>
				</div>
			) : (
				<>
					<MetricCard
						metric="customer_summary"
						tabMetric="customer_summary"
						cardRef={refs["customer_summary"]}
						classes="customer_summary"
					>
						{(metric, metricCardInViewport) => (
							<>
								<MetricSection
									metric={metric}
									metricCardInViewport={metricCardInViewport}
									metricSectionApi={fetchActiveCustomerMetrics}
									state={customerAnalytics}
								>
									{({ metrics }) => (
										<MultiChartMetrics>
											<ChartMetric
												size="large"
												loading={metrics?.active_customers?.metricsLoading}
												label="Active Customers"
												description="Customers who placed at least one order during the selected time range. Track this to know if your restaurant is still relevant and in demand — dips here may mean fewer customers are choosing you."
												value={metrics?.active_customers?.value}
												percentageChange={metrics?.active_customers?.percentageChange}
												compareValue={metrics?.active_customers?.compareValue}
											/>
										</MultiChartMetrics>
									)}
								</MetricSection>
								<MetricSection
									metric={metric}
									metricCardInViewport={metricCardInViewport}
									metricSectionApi={fetchRepeatCustomerMetrics}
									state={customerAnalytics}
								>
									{({ metrics }) => (
										<>
											<MultiChartMetrics>
												<ChartMetric
													size="large"
													loading={metrics?.repeat_customers?.metricsLoading}
													label="Repeat Customers"
													description="Customers who placed multiple orders during the selected time range. More repeat customers means stronger loyalty and higher LTV — drops here signal a need to revisit the experience."
													value={metrics?.repeat_customers?.value}
													percentageChange={metrics?.repeat_customers?.percentageChange}
													compareValue={metrics?.repeat_customers?.compareValue}
												/>
											</MultiChartMetrics>
										</>
									)}
								</MetricSection>
								<MetricSection
									metric={metric}
									metricCardInViewport={metricCardInViewport}
									metricSectionApi={fetchInActiveCustomerMetrics}
									state={customerAnalytics}
								>
									{({ metrics }) => (
										<>
											<MultiChartMetrics>
												<ChartMetric
													size="large"
													loading={metrics?.inactive_customers?.metricsLoading}
													label="Lapsed Customers"
													description="Customers who haven't placed an order in the selected time range, even though they ordered before. They've gone quiet — bring them back with offers, reminders, or a personal touch."
													value={metrics?.inactive_customers?.value}
													percentageChange={metrics?.inactive_customers?.percentageChange}
													compareValue={metrics?.inactive_customers?.compareValue}
												/>
											</MultiChartMetrics>
										</>
									)}
								</MetricSection>
							</>
						)}
					</MetricCard>
					<MetricCard metric="active_customers" cardRef={refs["active_customers"]} setCurrTab={setCurrTab}>
						{(metric, metricCardInViewport) => (
							<React.Fragment>
								<MetricSection
									metric={metric}
									metricCardInViewport={metricCardInViewport}
									metricSectionApi={fetchActiveCustomerMetrics}
									state={customerAnalytics}
								>
									{({ metrics, chartLoading, graphData }) => (
										<React.Fragment>
											<MetricHeader>
												<div className="header-left">
													<ChartMetric
														size="large"
														loading={metrics?.active_customers?.metricsLoading}
														label="Active Customers"
														description="Customers who placed at least one order during the selected time range. Track this to know if your restaurant is still relevant and in demand — dips here may mean fewer customers are choosing you."
														value={metrics?.active_customers?.value}
														percentageChange={metrics?.active_customers?.percentageChange}
														compareValue={metrics?.active_customers?.compareValue}
													/>
												</div>
												<div className="header-right">
													<DownloadButton
														emptyData={
															((graphData || []).length == 0 ||
																!metrics?.active_customers) &&
															!metrics?.active_customers?.metricsLoading &&
															!chartLoading
														}
														tooltipText="Export Chart as PNG"
														loading={
															metrics?.active_customers?.metricsLoading || chartLoading
														}
														handleClick={() => {
															!metrics?.active_customers?.metricsLoading &&
																!chartLoading &&
																(graphData || []).length !== 0 &&
																generateImage(
																	"active_customers",
																	"Active Customers",
																	"line"
																);
														}}
														coachMark={true}
													/>
												</div>
											</MetricHeader>
										</React.Fragment>
									)}
								</MetricSection>
								<MetricSection
									metric={metric}
									metricCardInViewport={metricCardInViewport}
									metricSectionApi={fetchActiveCustomersBreakdownChart}
									state={customerAnalytics}
								>
									{({ chartLoading, graphData, yScaleMax }, applDateFilter) => (
										<>
											<Line
												height={400}
												data={graphData}
												metric={metric}
												durationPreset={applDateFilter?.current?.dateFilter}
												loading={chartLoading}
												marginLeft={70}
												yScaleMax={yScaleMax}
												axisBottomTickRotation={graphData?.[0]?.data?.length > 7 ? -45 : 0}
												axisLeftLegendOffset={-60}
												enableArea={true}
												colors={["#2543B6"]}
												showCompareInTooltip={applDateFilter?.compare?.dateFilter}
												dashedLines={applDateFilter?.compare?.dateFilter}
												dashedLineIds={
													applDateFilter?.compare?.dateFilter ? [`Active Customers*`] : []
												}
												customAxisLeft={false}
												customAxisLeftCondition={(value) =>
													Number.isInteger(value) ? value : ""
												}
												legendItemWidth={applDateFilter?.compare?.dateFilter ? 130 : 200}
												yScaleStacked={false}
											/>
										</>
									)}
								</MetricSection>
							</React.Fragment>
						)}
					</MetricCard>
					<MetricCard
						metric="customers_breakdown"
						cardRef={refs["customers_breakdown"]}
						setCurrTab={setCurrTab}
					>
						{(metric, metricCardInViewport) => (
							<React.Fragment>
								<MetricSection
									metric={metric}
									metricCardInViewport={metricCardInViewport}
									metricSectionApi={fetchCustomersBreakdownChart}
									state={customerAnalytics}
									dependencies={[
										customerAnalytics[metric]?.selectedChart,
										customerAnalytics[metric]?.breakdownBy?.value
									]}
								>
									{(
										{
											chartLoading,
											selectedChart = "line",
											graphData,
											yScaleMax,
											tabularData,
											tableLoading
										},
										applDateFilter
									) => (
										<React.Fragment>
											<MetricHeader>
												<div className="header-left">
													<ChartMetric
														size="large"
														label="New vs. Repeat Customers"
														description="A healthy business brings in new customers and keeps existing ones. Use this to spot gaps — need more new users? Try promos. Losing repeats? Improve the experience."
													/>
												</div>
												<div className="header-right">
													<DownloadButton
														emptyData={
															(tabularData?.rows || []).length == 0 &&
															!tableLoading &&
															!chartLoading
														}
														tooltipText="Export"
														classes={
															(tabularData?.rows || []).length !== 0 &&
															!tableLoading &&
															!chartLoading
																? "center"
																: ""
														}
														loading={
															analyticsReportState?.customers_breakdown?.loading ||
															tableLoading ||
															chartLoading
														}
														options={[
															{ value: "png", valueForDisplay: "Export Chart as PNG" },
															{ value: "csv", valueForDisplay: "Export Table as CSV" }
														]}
														handleClick={(opt) => {
															if (opt == "csv") {
																!analyticsReportState?.customers_breakdown?.loading &&
																	!tableLoading &&
																	!chartLoading &&
																	(tabularData?.rows || []).length !== 0 &&
																	fetchAndGenerateCSVReport(
																		fetchCustomerBreakdownTable,
																		"getTabularData",
																		["customers_breakdown", true]
																	);
															} else if (opt == "png") {
																!chartLoading &&
																	generateImage(
																		"customers_breakdown",
																		"Total Customers Breakdown",
																		selectedChart
																	);
															}
														}}
													/>
												</div>
											</MetricHeader>
											{selectedChart == "line" && (
												<>
													<Line
														height={400}
														data={graphData?.line || []}
														durationPreset={applDateFilter?.current?.dateFilter}
														colors={{ datum: "color" }}
														loading={chartLoading}
														enableLegends={false}
														yScaleMax={yScaleMax}
														showCompareInTooltip={applDateFilter?.compare?.dateFilter}
														suffixedYValues={true}
														axisBottomTickRotation={
															graphData?.line?.[0]?.data?.length > 7 ? -45 : 0
														}
														dashedLines={applDateFilter?.compare?.dateFilter}
														dashedLineIndices={
															applDateFilter?.compare?.dateFilter ? "odd" : ""
														}
														showDashedLineOnHover={true}
													/>
												</>
											)}
										</React.Fragment>
									)}
								</MetricSection>
								<MetricSection
									metric={metric}
									metricCardInViewport={metricCardInViewport}
									metricSectionApi={fetchCustomerBreakdownTable}
									state={customerAnalytics}
									dependencies={[customerAnalytics[metric]?.sort]}
								>
									{({ tableLoading, tabularData, legends, sortedField, sort }) => (
										<>
											<CustomTable
												forAnalytics={true}
												loading={tableLoading}
												data={tabularData?.rows || []}
												columns={tabularData?.columns || []}
												legends={legends}
												sortList={(field) =>
													updateCustomerAnalyticsState(metric, {
														sort: {
															field: field,
															order: getSortOrder(sort, { field })
														},
														sortedField: field
													})
												}
												sortedField={sortedField}
												content="data"
												sortedOrder={sort?.order}
												classes="customers-breakdown-table-container"
											/>
										</>
									)}
								</MetricSection>
							</React.Fragment>
						)}
					</MetricCard>
					<MetricCard
						metric="new_customers_breakdown"
						cardRef={refs["new_customers_breakdown"]}
						setCurrTab={setCurrTab}
					>
						{(metric, metricCardInViewport) => (
							<>
								<MetricSection
									metric={metric}
									metricCardInViewport={metricCardInViewport}
									metricSectionApi={fetchNewCustomersBreakdownChart}
									state={customerAnalytics}
									dependencies={[
										customerAnalytics[metric]?.selectedChart,
										customerAnalytics[metric]?.breakdownBy?.value
									]}
								>
									{(
										{ chartLoading, selectedChart = "pie", graphData, yScaleMax, breakdownBy },
										applDateFilter
									) => (
										<React.Fragment>
											<MetricHeader>
												<div className="header-left">
													<ChartMetric
														size="large"
														label="New Customers Breakdown"
														description="Breakdown of first-time customers by the platform or brand they used."
													/>
												</div>
												<div className="header-right">
													<ChartSelector
														selectedChart={selectedChart}
														options={["pie", "line"]}
														clickHandler={(chart) => {
															updateCustomerAnalyticsState(metric, {
																selectedChart: chart
															});
															trackEvent("analytics_chart_type_switched", {
																module: "Customers",
																origin: metric,
																from: selectedChart,
																to: chart
															});
														}}
													/>

													<BreakdownByFilter
														currValue={breakdownBy}
														setFilter={(field, value) => {
															updateCustomerAnalyticsState(metric, {
																[field]: value
															});
															trackEvent("analytics_breakdown_selection", {
																module: "Customers",
																metric: metric,
																breakdownType: value.label
															});
														}}
														isMultibrandEnabled={isMultibrandEnabled}
														readOnly={
															!areMultiplePlatformsSelected || !areMultipleBrandsSelected
														}
													/>
													<DownloadButton
														handleClick={(opt) => {
															if (opt == "csv") {
																!analyticsReportState?.new_customers_breakdown
																	?.loading &&
																	!customerAnalytics?.new_customers_breakdown
																		?.tableLoading &&
																	(
																		customerAnalytics?.new_customers_breakdown
																			?.tabularData?.rows || []
																	).length !== 0 &&
																	fetchAndGenerateCSVReport(
																		fetchNewCustomersBreakdownTable,
																		"getTabularData",
																		["new_customers_breakdown", true]
																	);
															} else if (opt == "png") {
																!chartLoading &&
																	generateImage(
																		"new_customers_breakdown",
																		"New Revenue Breakdown",
																		selectedChart
																	);
															}
														}}
														options={[
															{ value: "png", valueForDisplay: "Export Chart as PNG" },
															{ value: "csv", valueForDisplay: "Export Table as CSV" }
														]}
														tooltipText="Export"
														emptyData={
															(
																customerAnalytics?.new_customers_breakdown?.tabularData
																	?.rows || []
															).length == 0 &&
															customerAnalytics?.new_customers_breakdown?.graphData?.[
																selectedChart
															].length == 0 &&
															!customerAnalytics?.new_customers_breakdown?.tableLoading &&
															!chartLoading
														}
														classes={
															(
																customerAnalytics?.new_customers_breakdown?.tabularData
																	?.rows || []
															).length !== 0 &&
															customerAnalytics?.new_customers_breakdown?.graphData?.[
																selectedChart
															].length !== 0 &&
															"center"
														}
														loading={
															analyticsReportState?.new_customers_breakdown?.loading ||
															customerAnalytics?.new_customers_breakdown?.tableLoading ||
															chartLoading
														}
													/>
												</div>
											</MetricHeader>
											<div className="chart-container">
												{selectedChart === "pie" && (
													<Pie
														height={400}
														marginTop={40}
														marginBottom={100}
														data={graphData?.pie || []}
														colors={[...ANALYTICS_DEFAULT_COLORS]}
														idKey="name"
														arcLabel="name"
														loading={chartLoading}
														enableCenterMetric={true}
														enableLegends={false}
														renderCenterMetricLabel={() => "New Customers"}
														renderCenterMetricValue={renderCenterMetricValue}
														sortByValue={false}
													/>
												)}
												{selectedChart === "line" && (
													<Line
														height={400}
														data={graphData?.line || []}
														durationPreset={applDateFilter?.current?.dateFilter}
														colors={{ datum: "color" }}
														showCompareInTooltip={applDateFilter?.compare?.dateFilter}
														dashedLines={applDateFilter?.compare?.dateFilter}
														dashedLineIndices={
															applDateFilter?.compare?.dateFilter ? "odd" : ""
														}
														showDashedLineOnHover={true}
														enableLegends={false}
														loading={chartLoading}
														yScaleMax={yScaleMax}
														suffixedYValues={true}
														axisBottomTickRotation={
															graphData?.line?.[0]?.data?.length > 7 ? -45 : 0
														}
													/>
												)}
											</div>
										</React.Fragment>
									)}
								</MetricSection>

								<MetricSection
									metric={metric}
									metricCardInViewport={metricCardInViewport}
									metricSectionApi={fetchNewCustomersBreakdownTable}
									state={customerAnalytics}
									dependencies={[
										customerAnalytics[metric]?.sort,
										customerAnalytics[metric]?.breakdownBy?.value
									]}
								>
									{({ tableLoading, tabularData, sortedField, sort, legends }) => (
										<CustomTable
											forAnalytics={true}
											loading={tableLoading}
											data={tabularData?.rows || []}
											columns={tabularData?.columns || []}
											sortList={(field) =>
												updateCustomerAnalyticsState(metric, {
													sort: {
														field: field,
														order: getSortOrder(sort, { field })
													},
													sortedField: field
												})
											}
											legends={legends}
											sortedField={sortedField}
											sortedOrder={sort?.order}
											classes="new-customers-breakdown-table-container"
											content="data"
										/>
									)}
								</MetricSection>
							</>
						)}
					</MetricCard>
				</>
			)}
		</div>
	);
};

export default connect((store) => ({
	analyticsFiltersState: store.analyticsFiltersState,
	isMultibrandEnabled: store.login.loggedInbizDetail.isMultibrandEnabled,
	bizId: store.login.loggedInbizDetail.id,
	analyticsReportState: store.analyticsReportState,
	login: store.login,
	configItems: store.configItems,
	customerAnalytics: store.customerAnalytics
}))(Customers);
